/**
 * Fills the wishlist page from the saved ids.
 *
 * The page ships with its empty state and this swaps in real cards, so a
 * visitor with an empty list sees a finished page rather than a spinner that
 * resolves to nothing. Shopify renders the cards, so prices, titles and
 * availability arrive correct for the active market and language.
 *
 * What changed after the first version, all of it visible to someone actually
 * using the page:
 *
 *   Removing a piece now removes its card. Before, the heart went hollow and
 *   the card stayed, and taking out the last one left a grid of nothing with
 *   the empty state still hidden underneath it.
 *
 *   Ids that no longer resolve are reported, never dropped. A product can be
 *   unpublished in one market and live in another, so pruning silently would
 *   delete a piece from an English visitor's list because it is only published
 *   in Italy. The page says how many are missing and offers to clear them; the
 *   customer decides.
 *
 *   Unavailable products are asked for explicitly. A wishlist that hides what
 *   is out of stock is the opposite of what a wishlist is for.
 *
 *   The swap is announced. A grid replacing itself under a screen reader is
 *   silent otherwise, so a separate status node says what happened rather than
 *   the container re-reading every card.
 */
import { read, has } from 'theme:wishlist';

const mount = document.querySelector('[data-wishlist-results]');
const empty = document.querySelector('[data-wishlist-empty]');
const status = document.querySelector('[data-wishlist-status]');
const notice = document.querySelector('[data-wishlist-missing]');

const strings = JSON.parse(
  document.querySelector('[data-wishlist-page-strings]')?.textContent ?? '{}'
);

/** Guards against two renders racing when a change lands mid-request. */
let token = 0;

function announce(message) {
  if (status && message) status.textContent = message;
}

function showEmpty() {
  if (mount) {
    mount.replaceChildren();
    mount.hidden = true;
  }
  if (empty) empty.hidden = false;
  if (notice) notice.hidden = true;
}

async function render() {
  if (!mount) return;

  const ids = read();
  const mine = ++token;

  if (!ids.length) {
    showEmpty();
    return;
  }

  const query = ids.map((id) => `id:${id}`).join(' OR ');
  const url =
    `${window.Shopify.routes.root}search` +
    `?type=product&q=${encodeURIComponent(query)}` +
    // A wishlist has to show what you saved even when it is sold out.
    `&options[unavailable_products]=show` +
    `&section_id=wishlist`;

  let html;
  try {
    const response = await fetch(url);
    html = response.ok ? await response.text() : null;
  } catch {
    html = null;
  }

  // A newer render started while this one was in flight.
  if (mine !== token) return;

  if (!html) {
    // Leaving whatever is on screen is the honest failure: it says nothing new
    // rather than replacing a working grid with an error.
    announce(strings.failed ?? '');
    return;
  }

  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const grid = parsed.querySelector('.product-grid');

  if (!grid) {
    showEmpty();
    announce(strings.empty ?? '');
    return;
  }

  mount.replaceChildren(grid);
  mount.hidden = false;
  if (empty) empty.hidden = true;

  // The cards arrived after the wishlist module painted its buttons.
  document.dispatchEvent(new CustomEvent('theme:carousel:refresh', { detail: { root: grid } }));

  const shown = grid.querySelectorAll('[data-wishlist-toggle]').length;
  const missing = ids.length - shown;

  if (notice) {
    notice.hidden = missing <= 0;
    const text = notice.querySelector('[data-wishlist-missing-text]');
    if (text && missing > 0) {
      text.textContent = (strings.missing ?? '').replace('%count%', String(missing));
    }
  }

  announce((strings.showing ?? '').replace('%count%', String(shown)));
}

/**
 * Clearing the unresolved ids is an explicit action, never automatic. It keeps
 * only the ids the last render actually resolved.
 */
notice?.querySelector('[data-wishlist-clear]')?.addEventListener('click', () => {
  const resolved = new Set(
    [...(mount?.querySelectorAll('[data-wishlist-toggle]') ?? [])].map((b) => b.dataset.wishlistToggle)
  );
  const kept = read().filter((id) => resolved.has(String(id)));
  try {
    localStorage.setItem('sofia:wishlist', JSON.stringify(kept));
  } catch {
    // Nothing to do: the list stays as it is and the notice stays visible.
  }
  document.dispatchEvent(new CustomEvent('theme:wishlist:change', { detail: { ids: kept } }));
});

document.addEventListener('theme:wishlist:change', () => render());

render();

export { has };
